import { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { OrgazoneMark, Trans } from '@orgazone/ui-web';
import { api, type RecoveryStatus } from '../api';
import { useAuth } from '../auth';
import { bandeauAvert, bandeauErr, carte, champ, ecran, label, lienAccent, note, eyebrow, heading } from './auth-chrome';
import { t } from '../i18n';

/**
 * CONNEXION AU BACK-OFFICE — deux chemins, et un seul est le chemin normal.
 *
 * · NORMAL : SSO Orgazone. Le bouton est un LIEN vers `/v1/auth/oidc/login`, une route SERVEUR — elle
 *   pose le cookie d'état PKCE avant de partir chez l'IdP. Y aller par le routeur ne ferait rien.
 * · SECOURS : connexion locale (bris de glace), pour ouvrir l'outil quand l'IdP est injoignable.
 *   ⚠️ Un compte fédéré ne passe PAS par là : son mot de passe vit à l'IdP, et le serveur le refuse
 *   sans le dire — message identique à « mot de passe faux » et à « compte inconnu » (pas d'énumération).
 *
 * Rien n'est simulé (§23.0) : le formulaire POSTe réellement `/v1/auth/login` et affiche le verdict du
 * serveur, y compris quand ce verdict est « je ne peux pas répondre ».
 */
export function Login() {
  const nav = useNavigate();
  const [sp] = useSearchParams();
  const { ready, mode, error, user, refresh } = useAuth();
  const degrade = sp.get('degraded') === '1'; // posé par le repli OIDC quand l'IdP est sondé injoignable

  const [mail, setMail] = useState('');
  const [pass, setPass] = useState('');
  const [remember, setRemember] = useState(false);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');
  const [recovery, setRecovery] = useState<RecoveryStatus | null>(null);

  // ⚠️ ATTENDRE `ready` avant toute redirection : sans cette garde, l'état INITIAL (session pas encore
  // résolue) déclenche un aller-retour /login → / → /login qui PERD la query string. Le repli
  // `?degraded=1` disparaissait alors, le bouton SSO se réaffichait, l'IdP était toujours injoignable…
  // → boucle entre les deux chemins, sans jamais dire pourquoi (défaut réel de l'EDI).
  useEffect(() => {
    if (!ready) return;
    if (user) nav('/', { replace: true });
  }, [ready, user, nav]);

  // Le parcours de réinitialisation n'est offert QUE si le serveur dit qu'il peut aboutir : un lien
  // « mot de passe oublié ? » sans messagerie derrière est un bouton qui fait semblant (§23.0).
  // ⚠️ Un échec de cette requête laisse `recovery` à `null` — on n'offre RIEN, et on ne l'annonce pas
  // ici : si cette route est injoignable, `/auth/mode` l'est aussi, et la panne est déjà dite par le
  // bandeau « service d'authentification injoignable ». Deux messages pour une seule panne diluent
  // l'information au lieu de l'ajouter.
  useEffect(() => {
    let vivant = true;
    api.recoveryStatus()
      .then((r) => { if (vivant) setRecovery(r); })
      .catch(() => { if (vivant) setRecovery(null); });
    return () => { vivant = false; };
  }, []);

  const soumettre = async (e: React.FormEvent) => {
    e.preventDefault();
    setErr('');
    setBusy(true);
    try {
      const r = await api.login(mail.trim(), pass, remember);
      // ⚠️ Le second facteur est du ressort de l'IdP : cette page de secours ne le gère pas, et elle le
      // DIT plutôt que d'afficher un champ qui ne serait relié à rien.
      if (r.status === '2fa_required') {
        setErr(t('Second facteur requis — non géré par cette page de secours. Passez par la connexion Orgazone (SSO).'));
        return;
      }
      await refresh();
      nav('/', { replace: true });
    } catch (e) {
      setErr(e instanceof Error && e.message ? e.message : t('Identifiants refusés.'));
    } finally {
      setBusy(false);
    }
  };

  const proposerSso = mode.oidc && !degrade;

  return (
    <div style={ecran}>
      <form onSubmit={soumettre} style={carte}>
        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', marginBottom: 22 }}>
          <div style={{ marginBottom: 12 }}><OrgazoneMark size={40} /></div>
          <div style={eyebrow}>{t('Back-office')}</div>
          <div style={heading}>{proposerSso ? t('Connexion') : t('Connexion locale')}</div>
        </div>

        {proposerSso && (
          <>
            {/* ⚠️ Un <a>, pas un bouton : navigation PLEINE PAGE vers une route serveur. */}
            <a href="/v1/auth/oidc/login" className="btn-cta" style={{ width: '100%', height: 46, marginBottom: 16 }}>
              
              {t('Se connecter avec Orgazone (SSO)')}
            </a>
            <div style={{ textAlign: 'center', fontSize: 'var(--t-dense)', color: 'var(--ink-400)', margin: '4px 0 14px' }}>
              
              {t('ou, en secours')}
            </div>
          </>
        )}

        {/* Service d'authentification injoignable : on le DIT. Sans ce signal, l'écran semble « refuser »
            la connexion alors que c'est le serveur qui ne répond pas — deux situations aux réponses
            opposées (recommencer, ou signaler une panne). */}
        {error && (
          <div style={bandeauErr}>
            <Trans k='<1>Service d’authentification injoignable.</1> La connexion ne peut pas aboutir pour l’instant : {error}' vars={{ error }} parts={[(x) => <strong>{x}</strong>]} />
          </div>
        )}

        {/* Mode dégradé : affiché SEULEMENT au repli, jamais en décor permanent. */}
        {degrade && (
          <div style={bandeauAvert}>
            <Trans k='<1>Authentification Orgazone indisponible.</1> Utilisez la <2>connexion locale de secours</2> (bris de glace). Réessayez le SSO plus tard.' parts={[(x) => <strong>{x}</strong>, (x) => <strong>{x}</strong>]} /></div>
        )}

        {err && <div style={{ ...bandeauErr, marginBottom: 12 }}>{err}</div>}

        <div style={{ marginBottom: 14 }}>
          <label style={label} htmlFor="bo-email">{t('Adresse e-mail')}</label>
          <input
            id="bo-email" style={champ} type="email" value={mail} autoComplete="username" required autoFocus
            onChange={(e) => { setMail(e.target.value); setErr(''); }}
          />
        </div>
        <div style={{ marginBottom: 14 }}>
          <label style={label} htmlFor="bo-pass">{t('Mot de passe')}</label>
          <input
            id="bo-pass" style={champ} type="password" value={pass} autoComplete="current-password" required
            onChange={(e) => { setPass(e.target.value); setErr(''); }}
          />
        </div>

        <label style={{ display: 'inline-flex', alignItems: 'center', gap: 8, fontSize: 'var(--t-lead)', color: 'var(--ink-500)', cursor: 'pointer', marginBottom: 18 }}>
          <input type="checkbox" checked={remember} onChange={() => setRemember((v) => !v)} />  {t('Se souvenir de moi')}
        </label>

        <button type="submit" disabled={busy} className="btn-cta" style={{ width: '100%', height: 46, cursor: busy ? 'wait' : undefined }}>
          {busy ? t('Connexion…') : t('Connexion locale')}
        </button>

        {/* ⚠️ TROIS états, jamais deux : praticable (on offre), impraticable (on dit pourquoi), pas encore
            su (on n'affiche rien — offrir un parcours dont on ignore s'il aboutit serait le pire des trois). */}
        {recovery?.available && (
          <button type="button" onClick={() => nav('/reset')} style={lienAccent}>
            
            {t('Mot de passe oublié ?')}
          </button>
        )}
        {recovery && !recovery.available && (
          <p style={{ ...note, marginTop: 14 }}>
            {/* ⚠️ DEUX PHRASES ENTIÈRES, parce que le motif peut MANQUER : `reason` est `string | null`,
                et un trou vide laissait la phrase finir sur un deux-points orphelin — « indisponible
                sur ce serveur : ». Invisible au typage tant que React rendait `null` comme rien,
                visible à l'écran (§23.0). Une clé à trou optionnel ferait porter la ponctuation au
                traducteur ; deux clés lui laissent écrire chaque phrase entière. */}
            {recovery.reason
              ? t('Réinitialisation par e-mail indisponible sur ce serveur : {reason}', { reason: recovery.reason })
              : t('Réinitialisation par e-mail indisponible sur ce serveur.')}
          </p>
        )}

        <p style={{ ...note, marginTop: 14 }}>
          
          {t('Accès réservé au personnel Orgazone. Le compte de secours ne sert qu’au dépannage.')}
        </p>
      </form>
    </div>
  );
}
